import { useCallback, useLayoutEffect } from "react";

/**
 * Restores scroll position from sessionStorage when the component mounts
 * (e.g. when returning from an edit page) and provides a wrapper to save
 * scroll before navigating away.
 *
 * Use in dashboard preview tabs so list scroll is restored after editing
 * an item. The scroll key should be listed in sessionStorageKeys and
 * cleared on login/logout.
 *
 * In production, content may render after first paint (e.g. data loading),
 * so we restore immediately and again after layout settles so the final
 * scroll position is correct.
 *
 * @param scrollKey - sessionStorage key used to save/restore scrollY
 * @returns saveScrollBefore - call with a callback (e.g. navigate); saves current scroll then runs the callback
 */
export function useSaveScrollOnReturn(scrollKey: string) {
  // Restore scroll on mount (e.g. when returning from edit page).
  // useLayoutEffect + behavior: 'instant' so the user never sees the wrong scroll.
  // Second restore (after rAF) helps in production when list content mounts after first paint.
  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem(scrollKey);
      if (saved !== null) {
        sessionStorage.removeItem(scrollKey);
        const y = Number(saved);
        if (!Number.isFinite(y) || y < 0) return;
        const scrollToY = () =>
          window.scrollTo({ top: y, left: 0, behavior: "instant" });
        scrollToY();
        const id = requestAnimationFrame(() => {
          requestAnimationFrame(scrollToY);
        });
        return () => cancelAnimationFrame(id);
      }
    } catch (error) {
      // ignore storage errors (e.g. private mode, quota)
      console.error("Error restoring scroll position from sessionStorage", error);
    }
  }, [scrollKey]);

  const saveScrollBefore = useCallback(
    (navigateCallback: () => void) => {
      try {
        sessionStorage.setItem(scrollKey, String(window.scrollY));
      } catch {
        // ignore storage errors
      }
      navigateCallback();
    },
    [scrollKey]
  );

  return saveScrollBefore;
}
